import { ExtrudeGeometry } from 'three';
import { Part } from './types';
import { computeTransforms, buildBendGeometry } from './geometry';
import { buildFlangeShape } from './flangeShape';
import { findActiveMiterPairs } from './miterPairs';

// ASCII STL writer. Walks every flange + bend, builds the same world-space
// triangle mesh shown in the 3D viewport, and emits each triangle as an STL
// facet with a recomputed face normal. ASCII STL is well-supported by Solid
// Edge ("Open" → mesh body / convergent body) and other CAD packages, and is
// easier to inspect than binary STL.
//
// Note: STL is a mesh format, not a parametric one. In Solid Edge the import
// produces a convergent (mesh) body — useful for visual reference and basic
// edits. For full sheet-metal parametric work, the existing DXF (flat pattern)
// is the recommended input.
export function exportStl(part: Part, solidName = 'BendSteelCal'): string {
  const transforms = computeTransforms(part);
  const miterPairs = findActiveMiterPairs(part);
  const out: string[] = [];
  out.push(`solid ${solidName}`);

  const fmt = (n: number) => {
    // STL parsers accept scientific notation, but plain decimals are friendlier
    // for diffing/debugging. 4 dp = 0.1 micron precision in mm, well below CAD
    // tolerance.
    if (!isFinite(n)) return '0';
    const r = Math.abs(n) < 1e-6 ? 0 : n;
    return r.toFixed(6).replace(/\.?0+$/, '') || '0';
  };

  const emitTri = (
    ax: number, ay: number, az: number,
    bx: number, by: number, bz: number,
    cx: number, cy: number, cz: number,
  ) => {
    // Per-facet normal from CCW triangle (right-hand rule).
    const ux = bx - ax, uy = by - ay, uz = bz - az;
    const vx = cx - ax, vy = cy - ay, vz = cz - az;
    let nx = uy * vz - uz * vy;
    let ny = uz * vx - ux * vz;
    let nz = ux * vy - uy * vx;
    const len = Math.hypot(nx, ny, nz);
    if (len > 1e-12) { nx /= len; ny /= len; nz /= len; } else { nx = ny = 0; nz = 1; }
    out.push(`  facet normal ${fmt(nx)} ${fmt(ny)} ${fmt(nz)}`);
    out.push(`    outer loop`);
    out.push(`      vertex ${fmt(ax)} ${fmt(ay)} ${fmt(az)}`);
    out.push(`      vertex ${fmt(bx)} ${fmt(by)} ${fmt(bz)}`);
    out.push(`      vertex ${fmt(cx)} ${fmt(cy)} ${fmt(cz)}`);
    out.push(`    endloop`);
    out.push(`  endfacet`);
  };

  // Triangles for a single geometry (positions + optional index buffer).
  const drainGeom = (g: { getAttribute: any; getIndex: any }) => {
    const pos = g.getAttribute('position');
    if (!pos) return;
    const idx = g.getIndex?.();
    if (idx) {
      for (let i = 0; i + 2 < idx.count; i += 3) {
        const a = idx.getX(i), b = idx.getX(i + 1), c = idx.getX(i + 2);
        emitTri(
          pos.getX(a), pos.getY(a), pos.getZ(a),
          pos.getX(b), pos.getY(b), pos.getZ(b),
          pos.getX(c), pos.getY(c), pos.getZ(c),
        );
      }
    } else {
      for (let i = 0; i + 2 < pos.count; i += 3) {
        emitTri(
          pos.getX(i), pos.getY(i), pos.getZ(i),
          pos.getX(i + 1), pos.getY(i + 1), pos.getZ(i + 1),
          pos.getX(i + 2), pos.getY(i + 2), pos.getZ(i + 2),
        );
      }
    }
  };

  const T = part.thickness;

  // Flange solids — extrude the in-plane outline (with miter cuts, reliefs, and
  // hole cutouts) by thickness, then move into world space using the precomputed
  // per-flange transform.
  for (const id of Object.keys(part.flanges)) {
    const xf = transforms[id];
    if (!xf) continue;
    const shape = buildFlangeShape(part, id, miterPairs);
    const g = new ExtrudeGeometry(shape, { depth: T, bevelEnabled: false });
    g.translate(0, 0, -T / 2);
    g.applyMatrix4(xf.matrix);
    drainGeom(g);
  }

  // Bend regions — already constructed in world coords by buildBendGeometry
  // (matches what the 3D viewport renders).
  for (const id of Object.keys(part.flanges)) {
    const f = part.flanges[id];
    if (!f.attachment) continue;
    const parentT = transforms[f.attachment.parentId];
    if (!parentT) continue;
    const g = buildBendGeometry(part, f.attachment.parentId, id, parentT);
    if (g) drainGeom(g);
  }

  out.push(`endsolid ${solidName}`);
  return out.join('\n') + '\n';
}

export function downloadStl(filename: string, content: string) {
  const blob = new Blob([content], { type: 'model/stl' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
